import React from 'react';
import { Search, Mail, BarChart2, Calendar, MoreHorizontal, Plus, Paperclip, Mic, ArrowUp, Sparkles } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Chat } from '../types';
import { ScrollArea } from './ScrollArea';
import { Avatar, AvatarFallback } from './Avatar';
interface ChatAreaProps {
  activeChat: Chat | null;
}
export function ChatArea({
  activeChat
}: ChatAreaProps) {
  if (activeChat) {
    return <ActiveChatView chat={activeChat} data-id="element-4" />;
  }
  return <EmptyStateView data-id="element-5" />;
}
function EmptyStateView() {
  return <div className="flex-1 flex flex-col items-center justify-center h-full max-w-3xl mx-auto w-full px-4" data-id="element-6">
      <h1 className="text-4xl font-semibold text-white mb-8 tracking-tight" data-id="element-7">
        What can I help you build today?
      </h1>

      <div className="w-full relative mb-6" data-id="element-8">
        <div className="bg-[#1c2033] border border-white/10 rounded-xl overflow-hidden focus-within:ring-1 focus-within:ring-[#4a4f6a] focus-within:border-[#4a4f6a] transition-all" data-id="element-9">
          <textarea placeholder="Assign a task or ask anything" className="w-full bg-transparent text-white p-4 min-h-[120px] resize-none outline-none placeholder:text-[#6b7194]" data-id="element-10" />
          <div className="flex items-center justify-between p-3 bg-[#1c2033]" data-id="element-11">
            <div className="flex items-center gap-1" data-id="element-12">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-white hover:bg-white/10 rounded-full" data-id="element-13">
                <Plus className="h-4 w-4" data-id="element-14" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-white hover:bg-white/10 rounded-full" data-id="element-15">
                <Paperclip className="h-4 w-4" data-id="element-16" />
              </Button>
            </div>
            <div className="flex items-center gap-2" data-id="element-17">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-white hover:bg-white/10 rounded-full" data-id="element-18">
                <Mic className="h-4 w-4" data-id="element-19" />
              </Button>
              <Button size="icon" className="h-8 w-8 bg-white text-black hover:bg-slate-200 rounded-full" data-id="element-20">
                <ArrowUp className="h-4 w-4" data-id="element-21" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 justify-center mb-12 w-full" data-id="element-22">
        <QuickActionButton icon={<Search className="h-4 w-4" data-id="element-24" />} label="Research" data-id="element-23" />
        <QuickActionButton icon={<Mail className="h-4 w-4" data-id="element-26" />} label="Write email" data-id="element-25" />
        <QuickActionButton icon={<BarChart2 className="h-4 w-4" data-id="element-28" />} label="Analyze data" data-id="element-27" />
        <QuickActionButton icon={<Calendar className="h-4 w-4" data-id="element-30" />} label="Schedule task" data-id="element-29" />
        <QuickActionButton icon={<MoreHorizontal className="h-4 w-4" data-id="element-32" />} label="More" data-id="element-31" />
      </div>

      <div className="w-full" data-id="element-33">
        <h3 className="text-sm font-medium text-slate-400 mb-3 px-1" data-id="element-34">
          Suggested for you
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-35">
          <SuggestionCard icon={<Mail className="h-4 w-4" data-id="element-37" />} title="Check Gmail for new leads" data-id="element-36" />
          <SuggestionCard icon={<BarChart2 className="h-4 w-4" data-id="element-39" />} title="Summarize this week's revenue" data-id="element-38" />
          <SuggestionCard icon={<Sparkles className="h-4 w-4" data-id="element-41" />} title="What should I focus on today?" data-id="element-40" />
          <SuggestionCard icon={<Search className="h-4 w-4" data-id="element-43" />} title="Research my top competitors" data-id="element-42" />
        </div>
      </div>
    </div>;
}
function ActiveChatView({
  chat
}: {
  chat: Chat;
}) {
  return <div className="flex-1 flex flex-col h-full w-full" data-id="element-44">
      {/* Header */}
      <div className="h-14 border-b border-white/5 flex items-center px-4 justify-between flex-shrink-0" data-id="element-45">
        <div className="font-medium text-slate-200" data-id="element-46">{chat.title}</div>
        <Button variant="ghost" size="icon" className="text-[#8b8fa8] hover:text-white" data-id="element-47">
          <MoreHorizontal className="h-5 w-5" data-id="element-48" />
        </Button>
      </div>

      {/* Chat Messages Area */}
      <ScrollArea className="flex-1 p-4" data-id="element-49">
        <div className="max-w-3xl mx-auto space-y-6 py-4" data-id="element-50">
          {/* Mock User Message */}
          <div className="flex gap-4 justify-end" data-id="element-51">
            <div className="bg-[#252a42] text-slate-200 px-4 py-3 rounded-2xl rounded-tr-sm max-w-[80%]" data-id="element-52">
              Can you help me with {chat.title.toLowerCase()}?
            </div>
            <Avatar className="h-8 w-8 rounded-md bg-[#252a42] flex-shrink-0 mt-1" data-id="element-53">
              <AvatarFallback className="bg-[#2a2f42] text-slate-300 rounded-md text-xs" data-id="element-54">
                J
              </AvatarFallback>
            </Avatar>
          </div>

          {/* Mock AI Message */}
          <div className="flex gap-4" data-id="element-55">
            <div className="h-8 w-8 rounded-md bg-emerald-900/50 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-56">
              <Sparkles className="h-4 w-4 text-emerald-400" data-id="element-57" />
            </div>
            <div className="text-slate-300 space-y-4 max-w-[80%] pt-1" data-id="element-58">
              <p data-id="element-59">
                I'd be happy to help you with that. Here's a quick overview of
                what we can do:
              </p>
              <ul className="list-disc pl-4 space-y-1 text-[#8b8fa8]" data-id="element-60">
                <li data-id="element-61">Analyze current data and trends</li>
                <li data-id="element-62">Draft necessary communications</li>
                <li data-id="element-63">Set up action items in your projects</li>
              </ul>
              <p data-id="element-64">What specific aspect would you like to tackle first?</p>
            </div>
          </div>
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="p-4 max-w-3xl mx-auto w-full" data-id="element-65">
        <div className="bg-[#1c2033] border border-white/10 rounded-xl flex items-end p-2 focus-within:ring-1 focus-within:ring-[#4a4f6a]" data-id="element-66">
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-white mb-1 flex-shrink-0" data-id="element-67">
            <Plus className="h-4 w-4" data-id="element-68" />
          </Button>
          <textarea placeholder="Reply to Chief of Staff..." className="flex-1 bg-transparent text-white p-2 max-h-[200px] min-h-[40px] resize-none outline-none placeholder:text-slate-500" rows={1} data-id="element-69" />
          <Button size="icon" className="h-8 w-8 bg-white text-black hover:bg-slate-200 rounded-full mb-1 flex-shrink-0" data-id="element-70">
            <ArrowUp className="h-4 w-4" data-id="element-71" />
          </Button>
        </div>
        <div className="text-center mt-2 text-xs text-[#6b7194]" data-id="element-72">
          AI can make mistakes. Consider verifying important information.
        </div>
      </div>
    </div>;
}
function QuickActionButton({
  icon,
  label
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return <Button variant="outline" className="bg-[#1c2033] border-white/10 text-[#8b8fa8] hover:bg-[#252a42] hover:text-white rounded-full h-9 px-4" data-id="element-73">
      <span className="mr-2 opacity-70" data-id="element-74">{icon}</span>
      {label}
    </Button>;
}
function SuggestionCard({
  icon,
  title
}: {
  icon: React.ReactNode;
  title: string;
}) {
  return <button className="flex items-center gap-3 p-4 rounded-xl border border-white/5 bg-[#1c2033]/50 hover:bg-[#1c2033] text-left transition-colors group" data-id="element-75">
      <div className="text-[#6b7194] group-hover:text-slate-300 transition-colors" data-id="element-76">
        {icon}
      </div>
      <span className="text-sm text-[#8b8fa8] group-hover:text-white transition-colors font-medium" data-id="element-77">
        {title}
      </span>
    </button>;
}